/**
 * Shared helpers for every budget model. Pure functions over AppData +
 * pay period; no model-specific branching here. Each model file
 * imports what it needs from this module.
 */

import type {
  AppData,
  ScheduledItem,
  Transaction,
} from '../../data/types';
import {
  addDays,
  diffInDays,
  fromIsoDate,
  isBetweenInclusive,
  startOfDay,
  toIsoDate,
} from '../date';
import type { PayPeriod } from '../payPeriod';
import { roundMoney as _roundMoney } from '../currency';
import { sumScheduledBillsInRange } from '../scheduledOccurrences';

export const AVG_DAYS_PER_MONTH = 30.4375;
export const AVG_DAYS_PER_YEAR = 365.25;

export const roundMoney = _roundMoney;

export const inPeriod = (period: PayPeriod, isoDate: string): boolean =>
  isBetweenInclusive(fromIsoDate(isoDate), period.start, period.end);

/** Effective last day of a spread transaction (clamped by spreadEndedOn). */
export function effectiveSpreadEnd(tx: Transaction): Date {
  const days = tx.spreadDays && tx.spreadDays > 1 ? tx.spreadDays : 1;
  const txStart = startOfDay(fromIsoDate(tx.date));
  const naturalEnd = addDays(txStart, days - 1);
  if (!tx.spreadEndedOn) return naturalEnd;
  const explicit = startOfDay(fromIsoDate(tx.spreadEndedOn));
  return explicit.getTime() < naturalEnd.getTime() ? explicit : naturalEnd;
}

/** Amount of `tx` that contributes to [start, end] inclusive (spread-aware). */
export function txContributionInRange(
  tx: Transaction,
  start: Date,
  end: Date,
): number {
  const days = tx.spreadDays && tx.spreadDays > 1 ? tx.spreadDays : 1;
  const txStart = startOfDay(fromIsoDate(tx.date));
  if (days === 1) {
    return isBetweenInclusive(txStart, start, end) ? tx.amount : 0;
  }
  const txEnd = effectiveSpreadEnd(tx);
  const lo = txStart > start ? txStart : start;
  const hi = txEnd < end ? txEnd : end;
  if (hi.getTime() < lo.getTime()) return 0;
  const overlap = diffInDays(lo, hi) + 1;
  return (tx.amount / days) * overlap;
}

export function txContributionOnDate(tx: Transaction, target: Date): number {
  return txContributionInRange(tx, target, target);
}

export const sumExpensesInRange = (
  txs: Transaction[],
  start: Date,
  end: Date,
): number =>
  roundMoney(
    txs
      .filter((t) => t.type === 'expense')
      .reduce((a, t) => a + txContributionInRange(t, start, end), 0),
  );

export const sumIncomeInRange = (
  txs: Transaction[],
  start: Date,
  end: Date,
): number =>
  roundMoney(
    txs
      .filter((t) => t.type === 'income')
      .reduce((a, t) => a + txContributionInRange(t, start, end), 0),
  );

export const sumExpensesInRangeWhere = (
  txs: Transaction[],
  start: Date,
  end: Date,
  predicate: (t: Transaction) => boolean,
): number =>
  roundMoney(
    txs
      .filter((t) => t.type === 'expense' && predicate(t))
      .reduce((a, t) => a + txContributionInRange(t, start, end), 0),
  );

export function resolveWorkDaysPerWeek(
  workDaysPerWeek: number | undefined,
): number {
  const n = typeof workDaysPerWeek === 'number' ? workDaysPerWeek : 5;
  if (n < 1) return 1;
  if (n > 7) return 7;
  return n;
}

export function isWorkday(date: Date, workDaysPerWeek: number): boolean {
  const wdpw = resolveWorkDaysPerWeek(workDaysPerWeek);
  if (wdpw >= 7) return true;
  const dow = date.getDay();
  return dow >= 1 && dow <= wdpw;
}

export function workdaysInRange(
  start: Date,
  end: Date,
  workDaysPerWeek: number,
): number {
  const startMs = startOfDay(start).getTime();
  const endMs = startOfDay(end).getTime();
  if (endMs < startMs) return 0;
  let count = 0;
  for (let ms = startMs; ms <= endMs; ms += 24 * 60 * 60 * 1000) {
    if (isWorkday(new Date(ms), workDaysPerWeek)) count += 1;
  }
  return count;
}

export const occurrencesInPeriod = (
  item: ScheduledItem,
  period: PayPeriod,
): number => {
  if (!item.active) return 0;
  switch (item.frequency) {
    case 'weekly':
      return period.totalDays / 7;
    case 'biweekly':
      return period.totalDays / 14;
    case 'monthly':
      return period.totalDays / AVG_DAYS_PER_MONTH;
    case 'yearly':
      return period.totalDays / AVG_DAYS_PER_YEAR;
    case 'one_time':
      return inPeriod(period, item.nextDate) ? 1 : 0;
  }
};

export const sumScheduledByType = (
  items: ScheduledItem[],
  period: PayPeriod,
  type: 'expense' | 'income',
): number =>
  roundMoney(
    items
      .filter((i) => i.type === type)
      .reduce((a, i) => a + i.amount * occurrencesInPeriod(i, period), 0),
  );

export function resolvePeriodIncome(data: AppData, period: PayPeriod): number {
  const oneOff = sumIncomeInRange(
    data.transactions,
    period.start,
    period.end,
  );
  const recurring = sumScheduledByType(data.scheduledItems, period, 'income');
  return roundMoney(oneOff + recurring);
}

/**
 * Spending that's actually left the user's pocket between [start, end]
 * inclusive. Combines:
 *   - Manual + spread expense transactions (txContributionInRange).
 *   - Scheduled-bill due dates that fall in the window — these
 *     auto-deduct on their due date, so the user never has to also
 *     log them as a transaction.
 */
export function sumSpendingWithScheduledInRange(
  data: AppData,
  start: Date,
  end: Date,
): number {
  const txs = sumExpensesInRange(data.transactions, start, end);
  const sch = sumScheduledBillsInRange(
    data.scheduledItems,
    toIsoDate(start),
    toIsoDate(end),
  );
  return roundMoney(txs + sch);
}

/** Pre-period rollover when profile.rollover === 'continuous'. */
export function computeRolloverAmount(
  data: AppData,
  period: PayPeriod,
): number {
  if (data.profile.rollover !== 'continuous') return 0;
  const horizon = new Date(1970, 0, 1);
  const dayBeforePeriod = addDays(period.start, -1);
  if (dayBeforePeriod.getTime() < horizon.getTime()) return 0;
  const pastIncome = sumIncomeInRange(
    data.transactions,
    horizon,
    dayBeforePeriod,
  );
  const pastExpense = sumExpensesInRange(
    data.transactions,
    horizon,
    dayBeforePeriod,
  );
  return roundMoney(pastIncome - pastExpense);
}
